import * as React from 'react';
import { EventHandler, ReactNode } from 'react';
import {
    useAutocomplete,
    UseAutocompleteProps,
} from '@mui/base/useAutocomplete';
import { Popper } from '@mui/base/Popper';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { cn } from '../../lib/helpers.ts';
import { useDebounce } from '@uidotdev/usehooks';
import { SchoolForAutocomplete } from '../../lib/types.ts';
import { IconAlertCircleFill, IconSpinner } from '../Icon.tsx';
import { useFetchAutocompleteSchools } from '../../lib/fetches.ts';
import { FormikErrors } from 'formik';

interface AutocompleteCustomProps {
    error?: string | null;
    isLoading?: boolean;
    placeholder?: string;
    onBlur: EventHandler<never>;
}

const Autocomplete = React.forwardRef(function Autocomplete(
    props: UseAutocompleteProps<
        any, // (typeof top100Films)[number],
        false,
        true,
        true
    > & { customProps: AutocompleteCustomProps },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const {
        disableClearable = false,
        disabled = false,
        readOnly = false,
        options,
        isOptionEqualToValue,
        ...other
    } = props;

    const {
        customProps: { error, isLoading, placeholder, onBlur },
    } = other;

    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        id,
        popupOpen,
        anchorEl,
        setAnchorEl,
        groupedOptions,
    } = useAutocomplete({
        ...props,
        componentName: 'Autocomplete',
        autoHighlight: true,
        filterOptions: (x) => x,
    });

    const rootRef = useForkRef(ref, setAnchorEl);

    return (
        <React.Fragment>
            <div {...getRootProps()} ref={rootRef}>
                <div className="relative">
                    <input
                        id={id}
                        disabled={disabled}
                        readOnly={readOnly}
                        placeholder={placeholder}
                        {...getInputProps()}
                        onBlur={onBlur}
                        className={cn(
                            'w-full rounded-xl border border-cloud-300 bg-cloud-200 px-4 py-3 font-sans text-base',
                            'autofill:font-sans autofill:text-base',
                            'focus:outline-none focus:ring-2 focus:ring-blue-600',
                            error && 'border-2 border-red-500'
                        )}
                    />

                    {isLoading && (
                        <IconSpinner
                            size={20}
                            className={cn(
                                'animate-spin text-blue-600',
                                'absolute right-4 top-[15px]'
                            )}
                        />
                    )}
                </div>
                {anchorEl && !isLoading && (
                    <Popper
                        open={popupOpen}
                        anchorEl={anchorEl}
                        slotProps={{
                            root: {
                                className: 'relative z-[1001] w-full max-w-2xl', // z-index: 1001 is needed to override ComponentPageTabs with z-index: 1000
                            },
                        }}
                        modifiers={[
                            { name: 'flip', enabled: false },
                            { name: 'preventOverflow', enabled: false },
                        ]}
                    >
                        <ul
                            {...getListboxProps()}
                            className={cn(
                                'z-[1] mx-0 my-3 box-border max-h-[300px] min-w-[320px] overflow-auto rounded-xl',
                                'border border-cloud-300 bg-white p-1.5 text-sm text-gray-900 shadow-sm outline-0',
                                'divide-y divide-cloud-300'
                            )}
                        >
                            {(groupedOptions as SchoolForAutocomplete[]).map(
                                (option, index) => {
                                    const optionProps = getOptionProps({
                                        option,
                                        index,
                                    });

                                    return (
                                        <li
                                            key={option.id}
                                            {...optionProps}
                                            className={cn(
                                                'group cursor-default list-none py-1.5 last-of-type:border-b-0'
                                            )}
                                        >
                                            <div
                                                className={cn(
                                                    'rounded-lg p-2 hover:cursor-pointer hover:bg-gray-100',
                                                    'group-ui-focused:bg-gray-100 group-ui-focused:text-gray-900',
                                                    'group-ui-focus-visible:bg-gray-100 group-ui-focus-visible:text-gray-900 group-ui-focus-visible:shadow-[0_0_0_3px_transparent] group-ui-focus-visible:shadow-blue-200',
                                                    'group-ui-focused:aria-selected:bg-blue-100 group-ui-focus-visible:aria-selected:bg-blue-100 group-ui-focused:aria-selected:text-blue-900 group-ui-focus-visible:aria-selected:text-blue-900',
                                                    'group-aria-selected:bg-blue-100 group-aria-selected:text-blue-900'
                                                )}
                                            >
                                                {option.name}
                                                <br />
                                                <span className="text-xs text-ink-800">
                                                    {option.street},{' '}
                                                    {option.city}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                }
                            )}

                            {groupedOptions.length === 0 && (
                                <li className="cursor-default list-none p-2">
                                    <p>
                                        Škola nebyla nalezena. Zkuste změnit
                                        hledaný výraz.
                                    </p>
                                </li>
                            )}
                        </ul>
                    </Popper>
                )}
                {error && (
                    <div className="mt-2 flex space-x-2.5 font-medium text-red-500">
                        <IconAlertCircleFill size={20} className="shrink-0" />
                        <p>{error}</p>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
});

interface FormInputProps {
    name: string;
    label: string | ReactNode | null;
    placeholder?: string;
    value: string; // TODO: check
    labelChildren?: ReactNode;
    error?: string | null;
    required?: boolean;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
    ) => Promise<void | FormikErrors>;
    setValues: (
        fields: React.SetStateAction<{
            [field: string]: any;
        }>,
        shouldValidate?: boolean
    ) => Promise<void | FormikErrors<Values>>;
    onBlur: EventHandler<never>;
    apiToFormNamesMap: object;
}

export default function AutocompleteSchools({
    name,
    label,
    placeholder,
    value,
    labelChildren,
    error,
    required,
    setFieldValue,
    setValues,
    onBlur,
    apiToFormNamesMap,
}: FormInputProps) {
    const debouncedQuery = useDebounce(value, 500);
    const { data: schools, isLoading: isLoadingSchools } =
        useFetchAutocompleteSchools(debouncedQuery);

    const inputValue = value ?? '';

    return (
        <div className="w-full">
            {label && (
                <div className="mb-0 flex w-full items-center justify-between">
                    <label htmlFor={name} className="block font-medium">
                        {label}{' '}
                        {required && <span className="text-red-500">*</span>}
                    </label>
                </div>
            )}
            <p key={name} className="!mt-0 mb-2 block text-sm text-ink-800">
                {labelChildren}
            </p>
            <Autocomplete
                id={name}
                customProps={{
                    error,
                    isLoading: isLoadingSchools,
                    placeholder,
                    onBlur,
                }}
                // clearOnBlur={false}
                // freeSolo={true}
                options={isLoadingSchools ? [] : schools ?? []}
                isOptionEqualToValue={(option, value) =>
                    option.id === value.id &&
                    option.name === value.name &&
                    inputValue === value.name
                }
                getOptionLabel={(option) => option?.name}
                onInputChange={(_, value) => {
                    setFieldValue(name, value);
                }}
                onChange={(_, value) => {
                    // for (const [key, val] of Object.entries(value)) {
                    //     if (key in apiToFormNamesMap) {
                    //         setFieldValue(
                    //             apiToFormNamesMap[
                    //                 key as keyof typeof apiToFormNamesMap
                    //             ],
                    //             val as string,
                    //             false
                    //         );
                    //     }
                    // }
                    // setTouched(
                    //     Object.keys(value).reduce(
                    //         (obj, key) => ({ ...obj, [key]: true }),
                    //         {}
                    //     )
                    // );
                    const stateValues = {};
                    for (const [key, val] of Object.entries(value)) {
                        if (key in apiToFormNamesMap) {
                            stateValues[
                                apiToFormNamesMap[
                                    key as keyof typeof apiToFormNamesMap
                                ]
                            ] = val;
                        }
                    }

                    setValues(stateValues);
                }}
            />
        </div>
    );
}
